import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { hideModalCustom } from "@src/redux/modalTypeCustom";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";
import { DOCUMENT_TYPES_BY_ENTITY } from "@src/utility/documentTypes";
import { fileToBase64 } from "@src/utility/fileToBase64";
import { CurrenciesIDEnum } from "@src/views/Balances/Components/currencies.types";

type RequestParams = Record<string, any>;

export interface CompaniesState {
  data: any;
  suggestions: any[];
  company: any | null;
  params: RequestParams;
  meta: any;
  total: number;
  countries: any[];
  states: any[];
  cities: any[];
}

export const getCompanies = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("companies/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    status: "all",
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get("api/companies", { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getCompaniesByContractor = createAsyncThunk<
  any,
  { id: number | string; params?: RequestParams },
  { state: RootState }
>("companies/getByContractor", async ({ id, params = {} }, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`api/contractors/${id}/companies`, {
    params: query
  });

  return {
    params: query,
    data: response.data
  };
});

export const getCompanySuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("companies/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get("api/companies/suggestions", {
    params: query
  });
  return {
    data: response.data
  };
});

export const getCompanyById = createAsyncThunk<any, number | string>(
  "companies/getById",
  async (id: number | string) => {
    const response = await api.get(`api/companies/${id}`);
    return {
      data: response.data
    };
  }
);

export const createCompany = createAsyncThunk<any, any, { dispatch: AppDispatch; state: RootState }>(
  "companies/create",
  async (payload, { dispatch, getState }) => {
    const state = getState();
    const { productSelected } = state.products || {};
    const { businessLineSelected } = state.businessLines || {};

    const mappedPayload = {
      name_company: payload.nombre_empresa,
      status: true,
      giro: payload.giro,
      email: payload.correo_electronico,
      phone: payload.telefono,
      password: payload.password,
      password_confirmation: payload.password_confirmation,
      razon_social: payload.razon_social,
      regimen_juridico: payload.tipo_entidad,
      nombres: payload.contacto_nombres,
      primer_apellido: payload.contacto_apellido_paterno,
      segundo_apellido: payload.contacto_apellido_materno,
      pais: payload.pais,
      estado: payload.estado,
      ciudad: payload.ciudad,
      codigo_postal: payload.codigo_postal,
      calle_numero: payload.calle_numero,
      rfc: payload.rfc,
      procedencia: "mexican",
      billing_type: payload.tipo_entidad,
      regimen_fiscal: payload.regimen_fiscal,
      product_id: productSelected?.value,
      business_line_id: businessLineSelected?.id,
      product: productSelected?.value,
      business_line: businessLineSelected?.id,
      uso_cfdi: 26,
      seller_id: payload.seller_id,
      colonia: payload.colonia || "NA",
      currency_id: CurrenciesIDEnum.MXN
    };

    const entityType = payload.tipo_entidad === "moral" ? "moral" : "physical";
    const documentsMap = DOCUMENT_TYPES_BY_ENTITY[entityType];
    const documentEntries = Object.entries(documentsMap);
    const documents = await Promise.all(
      documentEntries.map(async ([fieldName, documentType]) => {
        const file = payload[fieldName];
        if (!file || !documentType) return null;
        const base64 = await fileToBase64(file);
        return {
          document_type: documentType,
          original_name: file.name || fieldName,
          file: [base64]
        };
      })
    );

    const requestPayload = {
      ...mappedPayload,
      documents: documents.filter(Boolean)
    };

    const response = await api.post("api/companies", requestPayload);

    if (response.data.status === "success") {
        await dispatch(showModalSuccess({ message: response.data.message, url: "/companies"  }));
      }
      
    return {
      data: response.data
    };
  }
);

export const updateCompany = createAsyncThunk<
  any,
  { id: number | string; payload: any; returnUrl?: string },
  { dispatch: AppDispatch; state: RootState }
>("companies/update", async ({ id, payload, returnUrl }, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const mappedPayload = {
    estado: payload.estado,
    name_company: payload.nombre_empresa,
    status: true,
    giro: payload.giro,
    email: payload.correo_electronico,
    phone: payload.telefono,
    password: payload.password || undefined,
    razon_social: payload.razon_social,
    regimen_juridico: payload.tipo_entidad,
    nombres: payload.contacto_nombres,
    primer_apellido: payload.contacto_apellido_paterno,
    segundo_apellido: payload.contacto_apellido_materno,
    pais: payload.pais,
    ciudad: payload.ciudad,
    codigo_postal: payload.codigo_postal,
    calle_numero: payload.calle_numero,
    rfc: payload.rfc,
    procedencia: "mexican",
    billing_type: payload.tipo_entidad,
    regimen_fiscal: payload.regimen_fiscal,
    product_id: productSelected?.value,
    business_line_id: businessLineSelected?.id,
    product: productSelected?.value,
    business_line: businessLineSelected?.id,
    uso_cfdi: 26,
    seller_id: payload.seller_id,
    colonia: payload.colonia || "NA",
    currency_id: CurrenciesIDEnum.MXN
  };

  const sanitizedPayload = { ...mappedPayload } as Record<string, any>;
  Object.keys(sanitizedPayload).forEach((key) => {
    if (typeof sanitizedPayload[key] === "undefined") {
      delete sanitizedPayload[key];
    }
  });

  const entityType = payload.tipo_entidad === "moral" ? "moral" : "physical";
  const documentsMap = DOCUMENT_TYPES_BY_ENTITY[entityType];
  const documentEntries = Object.entries(documentsMap);
  const documents = await Promise.all(
    documentEntries.map(async ([fieldName, documentType]) => {
      const file = payload[fieldName];
      const isFile =
        file instanceof File || (file && typeof file === "object" && file.name);
      if (!isFile || !documentType) return null;
      const base64 = await fileToBase64(file);
      return {
        document_type: documentType,
        original_name: file.name || fieldName,
        file: [base64]
      };
    })
  );

  const requestPayload = {
    ...sanitizedPayload,
    documents: documents.filter(Boolean)
  };

  const response = await api.put(`api/companies/${id}`, requestPayload);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message, url: returnUrl ?? "/companies"  }));
  }

  return {
    data: response.data
  };
});

export const toggleCompanyStatus = createAsyncThunk<
  any,
  { id: number | string; deleted_at?: string | null },
  { dispatch: AppDispatch; state: RootState }
>("companies/toggleStatus", async ({ id, deleted_at }, { dispatch, getState }) => {
  const isActive = deleted_at === null || typeof deleted_at === "undefined";

  const response = isActive
    ? await api.delete(`api/companies/${id}`)
    : await api.patch(`api/active/companies/${id}`);

  if (response.data?.status === "success") {
    const { params } = getState().companies;
    dispatch(hideModalCustom());
    dispatch(getCompanies(params));
    dispatch(showModalSuccess({ message: response.data.message }));
  }

  return {
    data: response.data
  };
});

export const getCountriesOptions = createAsyncThunk(
  "companies/getCountries",
  async () => {
    const response = await api.get("api/countries", { params: { filter: "all" } });
    return {
      data: response.data
    };
  }
);

export const getStatesOptions = createAsyncThunk(
  "companies/getStates",
  async (countryId: number | string) => {
    const response = await api.get(`api/states/${countryId}`);
    return {
      data: response.data
    };
  }
);

export const getCitiesOptions = createAsyncThunk(
  "companies/getCities",
  async (stateId: number | string) => {
    const response = await api.get(`api/cities/${stateId}`);
    return {
      data: response.data
    };
  }
);

const initialState: CompaniesState = {
  data: [],
  suggestions: [],
  company: null,
  params: {},
  meta: {},
  total: 0,
  countries: [],
  states: [],
  cities: []
};

export const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    resetCompanies: (state) => {
      state.data = [];
      state.suggestions = [];
      state.company = null;
      state.params = {};
      state.meta = {};
      state.total = 0;
    },
    clearCompany: (state) => {
      state.company = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCompanies.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.params = action.payload.params;
      state.meta = action.payload.data?.meta;
      state.total = action.payload.data?.meta?.total;
    });
    builder.addCase(getCompaniesByContractor.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.params = action.payload.params;
      state.meta = action.payload.data?.meta;
      state.total = action.payload.data?.meta?.total;
    });
    builder.addCase(getCompanySuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data;
    });
    builder.addCase(getCompanyById.fulfilled, (state, action) => {
      state.company = action.payload?.data?.data ?? action.payload?.data ?? null;
    });
    builder.addCase(getCountriesOptions.fulfilled, (state, action) => {
      state.countries = action.payload.data;
    });
    builder.addCase(getStatesOptions.fulfilled, (state, action) => {
      state.states = action.payload.data;
    });
    builder.addCase(getCitiesOptions.fulfilled, (state, action) => {
      state.cities = action.payload.data;
    });
  }
});

export const { resetCompanies, clearCompany } = companiesSlice.actions;

export default companiesSlice.reducer;
